import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Switch, Divider, InputLabel, useTheme, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";

import FormInput from "../inputs/FormInput";
import { useCreateWarehouse } from "../../hooks/warehouses/useCreateWarehouse";
import { useUpdateWarehouse } from "../../hooks/warehouses/useUpdateWarehouse";

const FormAddEditWarehouse = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");
    const theme = useTheme();

    const [name, setName] = useState("");
    const [isDefault, setIsDefault] = useState(false);

    const { createWarehouseMutate, isCreating } = useCreateWarehouse();
    const { updateWarehouseMutate, isUpdating } = useUpdateWarehouse();
    const loadingSubmit = isCreating || isUpdating;

    useEffect(() => {
        if (isEdit && currentData) {
            setName(currentData?.name || "");
            setIsDefault(!!currentData?.is_default);
        } else {
            setName("");
            setIsDefault(false);
        }
    }, [isEdit, currentData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = { name, is_default: isDefault };
        const onDone = { onSuccess: () => onSubmitSuccess && onSubmitSuccess() };
        if (isEdit && currentData?.id) {
            updateWarehouseMutate({ id: currentData.id, data: payload }, onDone);
        } else {
            createWarehouseMutate(payload, onDone);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography variant="h6" fontWeight={700}>
                {isEdit
                    ? (isArabic ? "تعديل مستودع" : "Edit Warehouse")
                    : (isArabic ? "إضافة مستودع" : "Add Warehouse")}
            </Typography>

            <Divider />

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <FormInput
                        label={isArabic ? "الاسم" : "Name"}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                </Grid>

                <Grid item xs={12}>
                    <InputLabel sx={{ fontWeight: "bold", mb: 1 }}>
                        {isArabic ? "المستودع الافتراضي" : "Default Warehouse"}
                    </InputLabel>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2, py: 1,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="body2" fontWeight="medium">
                            {isDefault
                                ? (isArabic ? "افتراضي" : "Default")
                                : (isArabic ? "غير افتراضي" : "Not default")}
                        </Typography>
                        <Switch
                            checked={isDefault}
                            onChange={(e) => setIsDefault(e.target.checked)}
                            color="primary"
                        />
                    </Box>
                    {isDefault && !currentData?.is_default && (
                        <Alert severity="info" sx={{ mt: 1.5 }}>
                            {isArabic
                                ? "سيتم استبدال المستودع الافتراضي الحالي تلقائياً."
                                : "The current default warehouse will be replaced automatically."}
                        </Alert>
                    )}
                </Grid>
            </Grid>

            <Divider />

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Box
                    component="button"
                    type="button"
                    onClick={() => onSubmitSuccess && onSubmitSuccess()}
                    disabled={loadingSubmit}
                    sx={{
                        cursor: "pointer",
                        bgcolor: "transparent",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        px: 4, py: 1,
                        fontWeight: "bold",
                        color: "text.primary",
                    }}
                >
                    {isArabic ? "إلغاء" : "Cancel"}
                </Box>
                <Box
                    component="button"
                    type="submit"
                    disabled={loadingSubmit || !name}
                    sx={{
                        cursor: "pointer",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        border: "none",
                        borderRadius: 2,
                        px: 4, py: 1,
                        fontWeight: "bold",
                        minWidth: 160,
                        boxShadow: theme.shadows[2],
                        opacity: loadingSubmit || !name ? 0.6 : 1,
                    }}
                >
                    {loadingSubmit
                        ? (isArabic ? "جاري الحفظ..." : "Saving...")
                        : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "إنشاء" : "Create"))}
                </Box>
            </Box>
        </Box>
    );
};

export default FormAddEditWarehouse;
